/**
 * Axios Request Wrapper
 */
import axios from "axios";

import { setCookie } from "../shared/utils/cookie";
import { updateKeepAliveServiceWorkerToken } from "../shared/utils/serviceWorkerKeepAlive";
import { setLocalStorage } from "../shared/utils/localStorage";
import { getSessionStorage } from "../shared/utils/sessionStorage";
import {
  needsProactiveRenew,
  isExpiryFeatureEnabled,
  getTokenRefreshHandler,
  isGuestUser,
} from "../shared/utils/tokenExpiryHelpers";
import { isDynatraceApiErrorTrackingEnabled, logDTApiError } from "../shared/analytics/dynatrace";

import constants from "../shared/constants";
import storageConstants from "../shared/constants/storage";
import { API_HEADERS } from "../shared/constants/apiHeaders";
import errorConstants from "../shared/constants/error";

const { AVS_REFRESH_TOKEN, CANARY } = storageConstants;
const { AVS_ERROR_CODES } = errorConstants;

/**
 * Create an Axios Client
 */
const client = axios.create({
  baseURL: constants.API_BASE_URL,
});

/**
 * Check for connectivity
 */
const isOnline = () => navigator.onLine;

// Check if the request is to sessions endpoint
function isSessionsEndpoint(url) {
  const lower = (url || "").toLowerCase();
  return lower.includes("user/sessions") || lower.includes("user/devicesessions");
}

// Preflight to handle token renewal if needed
async function preflight(options) {
  if (isSessionsEndpoint(options?.url)) return;
  // Determine if near expiry, if not retrying for authorized API's invalid access token error
  if (!options._retriedAuthorizedApi && !needsProactiveRenew()) return;

  const handler = getTokenRefreshHandler();
  if (handler) {
    // pass preflight mode as true, if not retrying for authorized API's invalid access token error
    await handler(!options._retriedAuthorizedApi);
  }
}

/**
 * Request Wrapper with actions
 * NOTE: current Cancellation solution is based on CancelToken approach which is deprecated since axios v0.22.0, our axios is on v0.19.0 so AbortController may not be supported.
 * If we update axios in the future to greater than 0.22.0, we'll have to update to AbortController cancellation due to deprecation
 */
const request = function (options, cancelTokenSource = null, appProvider = null, usePromise = true) {
  // Check for internet connectivity before proceeding
  if (!isOnline()) {
    const offlineError = {
      message: "No internet connection. Please check your connectivity and try again.",
      type: "Network",
    };
    if (isDynatraceApiErrorTrackingEnabled()) {
      logDTApiError({
        method: options.method,
        url: options.url,
        status: 0,
        avsErrorCode: "ERR_OFFLINE",
        avsTransactionId: null,
        message: offlineError.message,
      });
    }
    console.error("Request aborted due to no internet connectivity:", offlineError);
    return usePromise ? Promise.reject(offlineError) : offlineError;
  }

  options.headers = options.headers ? { ...options.headers, restful: "yes" } : { restful: "yes" };
  if (getSessionStorage(CANARY)) {
    options.headers["x-deployment-target"] = "canary";
  }
  const onSuccess = function (response) {
    // Check header for AVS-refresh-token and store it
    if (response?.headers[AVS_REFRESH_TOKEN]) {
      setCookie(AVS_REFRESH_TOKEN, response.headers[AVS_REFRESH_TOKEN], 365);
      setLocalStorage(AVS_REFRESH_TOKEN, response.headers[AVS_REFRESH_TOKEN]);
      updateKeepAliveServiceWorkerToken(response.headers[AVS_REFRESH_TOKEN]);
    }
    if (options.returnResponseObject) {
      return response;
    } else {
      if (response?.data?.resultCode === "KO") {
        if (isDynatraceApiErrorTrackingEnabled()) {
          logDTApiError({
            method: options.method,
            url: options.url,
            status: response.status,
            avsErrorCode: response.data.errorDescription || response.headers?.[API_HEADERS.AVS_ERROR_DESCRIPTION],
            avsTransactionId: response.headers?.[API_HEADERS.AVS_TRANSACTION_ID],
            message: response.data.message,
          });
        }
        return usePromise
          ? Promise.reject({
              url: response.config?.url,
              message: response.data.message,
              type: "Server",
              code: response.data.errorDescription,
            })
          : {
              url: response.config?.url,
              message: response.data.message,
              type: "Server",
              code: response.data.errorDescription,
            };
      } else {
        return response?.data?.resultObj;
      }
    }
  };

  const onError = function (error) {
    if (axios.isCancel(error)) {
      // Silence is golden
      return;
    } else if (error) {
      // Reactive token renewal
      if (
        isExpiryFeatureEnabled() &&
        isGuestUser() === false &&
        error?.response?.data?.errorDescription === AVS_ERROR_CODES.TOKEN_NOT_VALID &&
        !isSessionsEndpoint(options?.url) &&
        !options._retriedAuthorizedApi
      ) {
        options._retriedAuthorizedApi = true;
        return request(options, cancelTokenSource, appProvider, usePromise); // retry once on 403-10100 error
      }

      console.error("Request Failed:", error);
      let errorResponse = null;
      const errorDetails = error.response;
      if (errorDetails) {
        console.error("Status:", errorDetails.status);
        console.error("Data:", errorDetails.data);
        console.error("Headers:", errorDetails.headers);
        if (options.returnResponseObject) {
          errorResponse = errorDetails;
        } else {
          errorResponse = {
            url: errorDetails.config.url,
            message: errorDetails.data.message,
            type: "Server",
            code: errorDetails.data.errorDescription,
          };
        }
        if (isDynatraceApiErrorTrackingEnabled()) {
          logDTApiError({
            method: options.method,
            url: options.url,
            status: errorDetails.status,
            avsErrorCode:
              errorDetails.data?.errorDescription || errorDetails.headers?.[API_HEADERS.AVS_ERROR_DESCRIPTION],
            avsTransactionId: errorDetails.headers?.[API_HEADERS.AVS_TRANSACTION_ID],
            message: errorDetails.data?.message,
          });
        }
      } else {
        console.error("Error Message:", error.message);
        errorResponse = error.message
          ? { url: error.url, message: error.message, type: "browser", code: error.code }
          : { message: error.request, type: "server" };
        if (isDynatraceApiErrorTrackingEnabled()) {
          logDTApiError({
            method: options.method,
            url: options.url,
            status: 0,
            avsErrorCode: error.code,
            avsTransactionId: null,
            message: error.message || error.request,
          });
        }
      }
      return Promise.reject(errorResponse);
    }
  };

  if (cancelTokenSource) options["cancelToken"] = cancelTokenSource.token;

  if (isExpiryFeatureEnabled() && isGuestUser() === false) {
    // Proactive token renewal
    return preflight(options)
      .then(() => client(options))
      .then(onSuccess)
      .catch(onError);
  } else {
    return client(options).then(onSuccess).catch(onError);
  }
};

export default request;
